import isEmail from 'validator/lib/isEmail';
import isURL from 'validator/lib/isURL';
import { decodeString, isNullOrUndefined } from '@utils/utils';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import {alpha_locale_safe, alphanumeric_locale_safe} from './alpha.helper';

dayjs.extend(customParseFormat);
dayjs.extend(isBetween)

export interface ErrorMessage {
  errorMessage: null | string,
  valid: boolean
}

export const validateForm = (itemRefs) => {
  let result = true;
  const keys = Object.keys(itemRefs).filter(key => key.includes('formRefs'));
  for (const key of keys) {

    if (!itemRefs[key].isValid()) {
      result = false;
    }
  }
  return result;
};


export const required = (value) => {
  if (value === null || value === undefined) {
    return false;
  }
  if (typeof value === 'string') {
    return value?.trim()?.length !== 0;
  } else {
    return value?.length !== 0;
  }
};

export const requiredIf = (value, targeted = undefined) => {
  const targetedExpectedValues = targeted?.split(',');
  const targetedElement = targetedExpectedValues?.shift();
  const targetElementValue = (document.getElementById(targetedElement) as HTMLInputElement)?.value;
  if (targetedExpectedValues?.length > 0) {
    if (targetedExpectedValues?.includes(targetElementValue)) {
      return required(value);
    } else {
      return true;
    }
  } else {
    if (targetElementValue) {
      return required(value);
    } else {
      return true;
    }
  }
};


export const numeric = (value) => {
  const ar = /^[٠١٢٣٤٥٦٧٨٩]+$/;
  const en = /^[0-9]+$/;
  if (isNullOrUndefined(value) || value === '') {
    return true;
  }
  const testValue = val => {
    const strValue = String(val);

    return en.test(strValue) || ar.test(strValue);
  };

  return testValue(value);
};


export const alpha_num = (value) => {
  if (isNullOrUndefined(value) || value === '') {
    return true;
  }

  return alphanumeric_locale_safe.test(value);
};


export const alpha = (value) => {
  // checks if the string contains any number or special characters so we can avoid locale issues
  if (isNullOrUndefined(value) || value === '') {
    return true;
  }

  return !alpha_locale_safe.test(value);
};

export const decimal = (value, {decimals = '2', separator = '.'} = {}) => {
  if (isNullOrUndefined(value) || value === '') {
    return true;
  }

  if (Number(decimals) === 0) {
    return /^-?\d*$/.test(value);
  }

  const regexPart = decimals === '*' ? '+' : `{1,${decimals}}`;
  const regex = new RegExp(`^[-+]?\\d*(\\${separator}\\d${regexPart})?([eE]{1}[-]?\\d+)?$`);

  if (!regex.test(value)) {
    return false;
  }

  const parsedValue = parseFloat(value);

  return parsedValue === parsedValue;
};

export const email = (value, {multiple = false, ...options} = {}) => {
  if (isNullOrUndefined(value) || value === '') {
    return true;
  }
  if (multiple && !Array.isArray(value)) {
    value = String(value).split(',').map(emailStr => emailStr.trim());
  }

  const validatorOptions = Object.assign({}, options);

  return isEmail(String(value), validatorOptions);
};

export const regex = (value, expression) => {
  if (typeof expression === 'string') {
    expression = new RegExp(expression);
  }
  return expression.test(String(value));
};

export const getFutureDateFromCurrentDay = (numOfDays: string) => {
  return dayjs().add(parseInt(numOfDays), 'day').format('DD-MM-YYYY');
};

/* START GENAI@GHCOPILOT */
export const dateBetween = (value: string, config:any) => {
  const configValue = config.split('|');
  const dateValue = dayjs(value, configValue[0], true);
  const futureStartDate = getFutureDateFromCurrentDay(configValue[2]);
  const futureEndDate = getFutureDateFromCurrentDay(configValue[1]);
  if (!futureStartDate || !futureEndDate) {
      return false;
  }
  const startDate = dayjs(futureStartDate, configValue[0], true);
  const endDate = dayjs(futureEndDate, configValue[0], true);

  if (isNullOrUndefined(value) || value === '') {
    return true;
  }

  if (!dateValue.isValid() || !startDate.isValid() || !endDate.isValid()) {
    return false;
  }

  return dateValue.isBetween(startDate, endDate, 'day', '[]');
}
/* END GENAI@GHCOPILOT */

export const dateFormat = (value:string, config:any) => {
  if (isNullOrUndefined(value) || value === '') {
    return true;
  }
  const dateValue = dayjs(value, config, true);

  return !!dateValue.isValid();
}

export const notWeekend = (value: string, config:any) => {
  const configArray = config.split('|');

  const dateValue = dayjs(value, configArray[0], true);
  const dateRange = JSON.parse(configArray[1]);

  const weekendDates = getWeekendDatesFromRange(dateRange?.[0] || 5, dateRange?.[1] || 14);
  return !weekendDates.includes(dateValue);
};

export const getWeekendDatesFromRange = (start: number, end: number) => {
  const weekendDayVal = [0, 6];
  const results = [];
  for (let startRange = start; startRange < end; startRange++) {
    const futureDate = dayjs().add(startRange, 'day');
    if (weekendDayVal.includes(futureDate.day())) {
      results.push(futureDate.format('DD-MM-YYYY'));
    }
  }
  return results;
};

export const notBefore = (value, config) => {
  const configNotBefore = config.split('|');
  const dateValue = dayjs(value, configNotBefore[0], true);
  const dateConfig = dayjs(configNotBefore[1], configNotBefore[0], true);

  if (isNullOrUndefined(value) || value === '') {
    return true;
  }
  if (!dateValue.isValid() || !dateConfig.isValid()) {
    return false;
  }
  return dateConfig.isBefore(dateValue);
};

export const notAfter = (value, config) => {
  const configArray = config.split('|');
  const dateValue = dayjs(value, configArray[0], true);
  const dateConfig = dayjs(configArray[1], configArray[0], true);

  if (isNullOrUndefined(value) || value === '') {
    return true;
  }
  if (!dateValue.isValid() || !dateConfig.isValid()) {
    return false;
  }
  return dateConfig.isAfter(dateValue);
};

export const maxRule = (value, max) => {
  if (isNullOrUndefined(value) || value === '') {
    return true;
  }

  return value?.length <= parseInt(max);
};

export const minRule = (value, min) => {
  if (isNullOrUndefined(value) || value === '') {
    return true;
  }

  return value?.length >= parseInt(min);
};

export const maxValue = (value, max) => {
  if (isNullOrUndefined(value) || value === '') {
    return true;
  }

  return Number(value) <= max;
};

export const minValue = (value, min) => {
  if (isNullOrUndefined(value) || value === '') {
    return true;
  }

  return Number(value) >= min;
};


export const url = (value) => {
  if (isNullOrUndefined(value) || value === '') {
    return true;
  }

  return isURL(value, {});
};

const luhnCheck = (value: string) => {
  /* START GENAI@CHATGPT4 */
  let arr = (value + '')
    .split('')
    .reverse()
    .map(x => parseInt(x));
  let lastDigit = arr.splice(0, 1)[0];
  let sum = arr.reduce((acc, val, i) => {
    if (i % 2 === 0) {
      val *= 2;
      if (val > 9) val -= 9;
    }
    return acc + val;
  }, 0);
  sum += lastDigit;
  return sum % 10 === 0;
  /* END GENAI@CHATGPT4 */
}

export const cardCheck = (value) => {
  if (value && value.toString().length === 16) {
    let expression = /^4[0-9]{12}(?:[0-9]{3,6})?$/;
    return expression.test(value) && luhnCheck(value);
  } else {
    return false;
  }
};

export const confirm = (value, targetElement) => {
  if (isNullOrUndefined(value) || value === '') {
    return true;
  }

  return String(value) === (document.getElementById(targetElement) as HTMLInputElement).value;
};


function toDate(dateStr) {
  let splitDateStr = dateStr.split('-');
  return new Date(splitDateStr[2], splitDateStr[1] - 1, splitDateStr[0]);
}

export const isWeekend = (value) => {
  let date = toDate(value);
  return (date.getDay() === 6 || date.getDay() === 0);
};

export const isWeekday = (value) => {
  return !isWeekend(value);
};

export const checkboxAllRequired = (value) => {
  return (Number(value) === 0);
};

export const checkboxOneRequired = (value) => {
  return (Number(value) > 0);
};
export const MD_LINK_REGEX = /\[([^\[]+)](\(.*\))/gm;

/* START GENAI@CHATGPT4 */
export const decodeHTMLEntities = (value) => {
  // decode HTML entities and also convert markdown links to HTML links if detected
  const decodedValue = decodeString(value);
  if (decodedValue?.match(MD_LINK_REGEX)) {
    return decodedValue?.replace(/\[([^\[]+)](\(([^)]*))\)/gim, '<a href="$3" target="_blank" rel="noopener noreferrer">$1 <span class="visa-icon visa-icon-new-window vs-font-size-standard"></span></a>');
  }
  return decodedValue;
};
/* END GENAI@CHATGPT4 */
